import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockMarketData } from '../mockData';
import BottomNav from '../components/BottomNav';
import Logo from '../components/Logo';
import CryptoIcon from '../components/CryptoIcon';
import CryptoTicker from '../components/CryptoTicker';
import EmailModal from '../components/EmailModal';
import InstallAppButton from '../components/InstallAppButton';
import { Bitcoin, TrendingUp, TrendingDown, DollarSign, Coins, Gem, Mail, Globe, Eye, EyeOff, Settings } from 'lucide-react';
import { useBalanceVisibility } from '../contexts/BalanceVisibilityContext';
import useSmoothLoading, { useSilentLoading } from '../hooks/useSmoothLoading';

const Home = () => {
  const navigate = useNavigate();
  const { balanceVisible, toggleBalanceVisibility, formatBalance } = useBalanceVisibility();
  const [activeTab, setActiveTab] = useState('hot');
  const [marketData, setMarketData] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const dataLoadedRef = useRef(false);
  
  // Use smooth loading for initial load, silent loading for refreshes
  const { showLoading, startLoading, stopLoading } = useSmoothLoading({ showDelay: 500 });
  const { withSilentLoading } = useSilentLoading();

  const handleMailClick = () => {
    try {
      window.location.href = 'mailto:customer-service@edgetrades.site?subject=Support Request - EdgeTrade&body=Hello EdgeTrade Support Team,%0D%0A%0D%0AI need assistance with:%0D%0A%0D%0A';
      setTimeout(() => {
        setShowEmailModal(true);
      }, 1000);
    } catch (error) {
      setShowEmailModal(true);
    }
  };

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    // Fetch market data with smart loading
    const fetchMarketData = async (silent = false) => {
      const loadingFn = silent ? withSilentLoading : (fn) => {
        startLoading();
        return fn().finally(() => stopLoading());
      };

      await loadingFn(async () => {
        try {
          const { marketAPI } = await import('../services/api');
          const response = await marketAPI.getPrices('all');
          
          // Only update if data changed (prevents unnecessary re-renders)
          setMarketData(prev => {
            const hasChanged = JSON.stringify(prev) !== JSON.stringify(response.data);
            if (hasChanged || !dataLoadedRef.current) {
              dataLoadedRef.current = true;
              return response.data;
            }
            return prev;
          });
        } catch (error) {
          console.error('Error fetching market data:', error);
          // Fallback to mock data only on first load
          if (!dataLoadedRef.current) {
            setMarketData(mockMarketData);
            dataLoadedRef.current = true;
          }
        }
      });
    };

    // Initial fetch with loading indicator
    fetchMarketData(false);

    // Refresh prices every 30 seconds silently (no loading indicator)
    const interval = setInterval(() => fetchMarketData(true), 30000);
    return () => clearInterval(interval);
  }, [navigate, startLoading, stopLoading, withSilentLoading]);

  const filterData = () => {
    switch(activeTab) {
      case 'hot':
        return marketData;
      case '24h':
        return [...marketData].sort((a, b) => Math.abs(b.change) - Math.abs(a.change));
      case 'rise':
        return marketData.filter(item => item.change > 0).sort((a, b) => b.change - a.change);
      case 'loss':
        return marketData.filter(item => item.change < 0).sort((a, b) => a.change - b.change);
      default:
        return marketData;
    }
  };

  const getCryptoColorClass = (symbol) => {
    const upperSymbol = symbol.split('/')[0].toUpperCase();
    const colorMap = {
      'BTC': 'crypto-btc',
      'ETH': 'crypto-eth',
      'BNB': 'crypto-bnb',
      'USDT': 'crypto-usdt',
      'XRP': 'crypto-xrp',
      'SOL': 'crypto-sol',
      'DOGE': 'crypto-doge',
      'ADA': 'crypto-ada',
      'GOLD': 'crypto-gold'
    };
    return colorMap[upperSymbol] || 'crypto-default';
  };

  const getCryptoIcon = (symbol) => {
    switch(symbol.split('/')[0]) {
      case 'BTC': return <Bitcoin className="w-6 h-6 text-orange-400" />;
      case 'ETH': return <Gem className="w-6 h-6 text-purple-400" />;
      case 'GOLD': return <Coins className="w-6 h-6 text-yellow-400" />;
      case 'USDT':
      case 'USDC': return <DollarSign className="w-6 h-6 text-green-400" />;
      default: return <TrendingUp className="w-6 h-6 text-cyan-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-cyan-950 to-blue-900 pb-20">
      {/* Crypto Ticker */}
      <CryptoTicker position="top" autoScroll={true} />
      
      {/* Header */}
      <div className="sticky top-0 bg-gradient-to-br from-slate-950/95 via-blue-950/95 to-cyan-950/95 backdrop-blur-sm z-10 border-b border-cyan-500/20">
        <div className="flex items-center justify-between px-4 py-4">
          <Logo size="small" showText={true} />
          <div className="flex gap-1 sm:gap-2">
            <button 
              onClick={handleMailClick}
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-cyan-500/20 hover:bg-cyan-500/30 flex items-center justify-center neon-icon transition-all"
              title="Email Support"
            >
              <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
            </button>
            <button className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-cyan-500/20 hover:bg-cyan-500/30 flex items-center justify-center neon-icon transition-all">
              <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
            </button>
            <button 
              onClick={() => navigate('/settings')}
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-cyan-500/20 hover:bg-cyan-500/30 flex items-center justify-center neon-icon transition-all"
            >
              <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
            </button>
          </div>
        </div>

        {/* Banner */}
        <div className="px-4 pb-4">
          <div className="bg-gradient-to-r from-cyan-900/30 via-blue-900/30 to-cyan-900/30 border-2 border-cyan-500/50 rounded-xl p-4 flex items-center justify-between golden-neon-border">
            <div>
              <h3 className="font-black text-white mb-1 golden-neon-text">STABLE INCOME</h3>
              <p className="text-xs text-white font-bold">USE LOW-RISK TRADING PRODUCTS FROM<br/>LIVING AND INVESTING RETURN</p>
            </div>
            <Coins className="w-12 h-12 text-cyan-400 golden-neon-icon" />
          </div>
        </div>

        {/* Install App Button */}
        <div className="px-4 pb-4 flex justify-center">
          <InstallAppButton />
        </div>

        {/* Tabs */}
        <div className="flex gap-6 px-4 pb-3">
          <div className="live-indicator">
            <span className="text-white text-xs font-black">LIVE</span>
          </div>
          {['hot', '24h', 'rise', 'loss'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`text-sm font-black pb-2 border-b-3 transition-all ${
                activeTab === tab
                  ? 'border-cyan-500 text-white golden-neon-text'
                  : 'border-transparent text-white hover:text-white'
              }`}
            >
              {tab === 'hot' && 'HOT'}
              {tab === '24h' && '24H LIST'}
              {tab === 'rise' && 'RISE'}
              {tab === 'loss' && 'LOSS'}
            </button>
          ))}
        </div>
      </div>

      {/* Market List */}
      <div className="px-4 py-2">
        {showLoading && !dataLoadedRef.current ? (
          <div className="text-center py-12">
            <TrendingUp className="w-12 h-12 text-cyan-400 mx-auto mb-4 animate-pulse golden-neon-icon" />
            <p className="text-white font-bold">LOADING MARKET DATA...</p>
          </div>
        ) : (
          filterData().map(item => (
            <div
              key={item.id}
              onClick={() => navigate('/binary-trade', { state: { selectedPair: item.symbol } })}
              className="crypto-card flex items-center justify-between py-4 border-purple-500/30 hover:bg-purple-600/20 transition-all cursor-pointer rounded-lg px-3 mb-2"
              style={{
                background: 'linear-gradient(135deg, rgba(109, 40, 217, 0.15) 0%, rgba(124, 58, 237, 0.1) 100%)',
                border: '1px solid rgba(139, 92, 246, 0.3)'
              }}
            >
              <div className="flex items-center gap-3">
                <CryptoIcon symbol={item.symbol.split('/')[0]} size={32} />
                <div>
                  <div className="font-black text-base text-white golden-neon-text">{item.symbol}</div>
                  <div className="text-xs text-white font-bold">{item.name.toUpperCase()}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-black text-lg golden-price-text">${item.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-black gap-1 border-2 ${
                  item.change >= 0 
                    ? 'bg-green-500/20 text-green-400 border-green-500/50 shadow-lg shadow-green-500/30' 
                    : 'bg-red-500/20 text-red-400 border-red-500/50 shadow-lg shadow-red-500/30'
                }`}>
                  {item.change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}%
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <BottomNav active="home" />
      <EmailModal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)} />
    </div>
  );
};

export default Home;